import {
  AvatarArmsItemNo,
  AvatarAurasItemNo,
  AvatarBodiesItemNo,
  AvatarFeetItemNo,
  AvatarHatsItemNo,
  AvatarNecksItemNo,
  AvatarWingsItemNo,
} from '../../constants/AvatarItem';
import { RegionsCode } from '../../constants/region';
import {
  AttachmentImage,
  AttachmentImageExample,
  EducationLevel,
  UserId,
} from '../Base';
import { RepositoryElement } from '../Repository';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { getLocalCurrencyById } from '../../utils/region';

export enum Gender {
  Male = 'male',
  Female = 'female',
  Other = 'other',
}

export interface Avatar {
  body: AvatarBodiesItemNo | null;
  hat: AvatarHatsItemNo | null;
  arms: AvatarArmsItemNo | null;
  feet: AvatarFeetItemNo | null;
  wings: AvatarWingsItemNo | null;
  neck: AvatarNecksItemNo | null;
  aura: AvatarAurasItemNo | null;
}

export enum UserRole {
  TrialUser = 'trialUser',
  User = 'user',
  VerifiedUser = 'verifiedUser',
  Staff = 'staff',
  Sales = 'sales',
  Admin = 'admin',
  Partner = 'partner',
  Dummy = 'dummy',
  WaitingListUser = 'waitingListUser',

  System = 'system',
}

export enum PrivacyScope {
  Public = 'public',
  Members = 'members',
  Self = 'self',
}

interface PrivacySettings {
  birthday?: PrivacyScope;
  gender?: PrivacyScope;
  industryOfWork?: PrivacyScope;
  organization?: PrivacyScope;
  level?: PrivacyScope;
  region?: PrivacyScope;
  topics?: PrivacyScope;
  yearOfBirth?: PrivacyScope;
  ageGroup?: PrivacyScope;
}

interface CharmJson {
  plays?: number;
  likes?: number;
}

class Charm {
  plays: number;
  likes: number;

  constructor(charm?: CharmJson) {
    this.plays = charm?.plays ?? 0;
    this.likes = charm?.likes ?? 0;
  }

  get total(): number {
    return this.plays + this.likes;
  }
}

interface SocialContributionJson {
  browsingActivities?: number;
  volunteerActivities?: number;
  givingCheers?: number;
  esgPlants?: number;
  encouragements?: number;
  multiplayerGames?: number;
  gameCreations?: number;
  postGames?: number;
}

class SocialContribution {
  browsingActivities: number;
  volunteerActivities: number;
  givingCheers: number;
  esgPlants: number;
  encouragements: number;
  multiplayerGames: number;
  gameCreations: number;
  postGames: number;

  constructor(socialContribution?: SocialContributionJson) {
    this.browsingActivities = socialContribution?.browsingActivities ?? 0;
    this.volunteerActivities = socialContribution?.volunteerActivities ?? 0;
    this.givingCheers = socialContribution?.givingCheers ?? 0;
    this.esgPlants = socialContribution?.esgPlants ?? 0;
    this.encouragements = socialContribution?.encouragements ?? 0;
    this.multiplayerGames = socialContribution?.multiplayerGames ?? 0;
    this.gameCreations = socialContribution?.gameCreations ?? 0;
    this.postGames = socialContribution?.postGames ?? 0;
  }

  get total(): number {
    return (
      this.browsingActivities +
      this.givingCheers +
      this.volunteerActivities +
      this.esgPlants +
      this.encouragements +
      this.multiplayerGames +
      this.gameCreations +
      this.postGames
    );
  }
}

interface KnowledgeJson {
  games?: number;
  slideshows?: number;
  competitions?: number;
}

class Knowledge {
  games: number;
  slideshows: number;
  competitions: number;

  constructor(knowledge?: KnowledgeJson) {
    this.games = knowledge?.games ?? 0;
    this.slideshows = knowledge?.slideshows ?? 0;
    this.competitions = knowledge?.competitions ?? 0;
  }

  get total(): number {
    return this.games + this.slideshows + this.competitions;
  }
}

interface ExpJson {
  charm?: CharmJson;
  socialContribution?: SocialContributionJson;
  knowledge?: KnowledgeJson;
}

export class Exp {
  charm: Charm;
  socialContribution: SocialContribution;
  knowledge: Knowledge;

  constructor(exp?: ExpJson) {
    this.charm = new Charm(exp?.charm);
    this.socialContribution = new SocialContribution(exp?.socialContribution);
    this.knowledge = new Knowledge(exp?.knowledge);
  }

  get total(): number {
    return (
      this.charm.total + this.knowledge.total + this.socialContribution.total
    );
  }

  get level(): number {
    return XpLevel.getLevel(this.total);
  }

  get progress(): number {
    return XpLevel.getProgress(this.total);
  }

  get currentXp(): number {
    return XpLevel.getRemainingExp(this.total);
  }

  get nextLevelRequirement(): number {
    if (this.level == XpLevel.maxLevel) {
      return 0;
    }
    return XpLevel.getLevelUpRequirement(this.level);
  }

  get totalNextLevelRequirement(): number {
    if (this.level == XpLevel.maxLevel) {
      return 0;
    }
    return XpLevel.getLevelUpTotalRequirement(this.level);
  }
}

class XpLevel {
  static maxLevel: number = 1000;
  static xpLevels = [
    { xp: 100, count: 4 },
    { xp: 120, count: 4 },
    { xp: 150, count: 4 },
    { xp: 200, count: 4 },
    { xp: 500, count: 4 },
    { xp: 1000, count: 4 },
    { xp: 1200, count: 4 },
    { xp: 1500, count: 4 },
    { xp: 2000, count: 4 },
    { xp: 2500, count: 4 },
    { xp: 3000, count: 8 },
    { xp: 3500, count: 8 },
    { xp: 4000, count: 9 },
    { xp: 5000, count: 934 },
  ];

  static getLevel(total: number): number {
    let level = 1;
    let remainingExp = total;

    for (const { xp, count } of this.xpLevels) {
      if (remainingExp > xp * count) {
        level += count;
        remainingExp -= xp * count;
      } else {
        level += Math.floor(remainingExp / xp);
        break;
      }
    }

    return Math.min(level, this.maxLevel);
  }

  static getRemainingExp(total: number): number {
    const xp = { level: 1, remainingExp: total };
    const result = this.xpLevels.reduce((acc, stage) => {
      const stageExp = stage.xp * stage.count;
      const newAcc = { ...acc };

      if (newAcc.remainingExp > stageExp) {
        newAcc.level += stage.count;
        newAcc.remainingExp -= stageExp;
      } else if (newAcc.remainingExp > 0) {
        const level = Math.floor(newAcc.remainingExp / stage.xp);
        newAcc.level += level;
        newAcc.remainingExp -= level * stage.xp;
      }

      return newAcc;
    }, xp);

    return result.remainingExp;
  }

  static getLevelUpTotalRequirement(currentLevel: number): number {
    let acc = currentLevel;
    let total = 0;
    for (const level of this.xpLevels) {
      acc -= level.count;
      if (acc < 0) {
        return total + (level.count + acc) * level.xp;
      }
      total += level.count * level.xp;
    }
    return total;
  }

  static getLevelUpRequirement(currentLevel: number): number {
    let acc = currentLevel;
    for (const level of this.xpLevels) {
      acc -= level.count;
      if (acc < 0) {
        return level.xp;
      }
    }
    return this.xpLevels[this.xpLevels.length - 1]?.xp || 0;
  }

  static getProgress(total: number): number {
    const xp = { level: 1, remainingExp: total };
    const result = this.xpLevels.reduce((acc, stage) => {
      const stageExp = stage.xp * stage.count;
      const newAcc = { ...acc };

      if (newAcc.remainingExp > stageExp) {
        newAcc.level += stage.count;
        newAcc.remainingExp -= stageExp;
      } else if (newAcc.remainingExp > 0) {
        const level = Math.floor(newAcc.remainingExp / stage.xp);
        newAcc.level += level;
        newAcc.remainingExp -= level * stage.xp;
      }

      return newAcc;
    }, xp);
    const requirement = this.getLevelUpRequirement(result.level);
    return result.remainingExp / requirement;
  }
}

type StatisticsKey =
  // file storage
  | 'storedImageCount'
  | 'storedImageSize'
  | 'storedIconCount'
  | 'storedIconSize'
  | 'storedAudioCount'
  | 'storedAudioSize'
  | 'storedVideoCount'
  | 'storedVideoSize'
  // created content
  | 'createdGameCollectionCount'
  | 'createdGameCount'
  | 'createdSlideshowCount'
  // classroom related
  | 'rootPublicClassroomCount'
  | 'rootPrivateAndVisibleClassroomCount'
  | 'rootPrivateAndHiddenClassroomCount'
  | 'rootPersonalClassroomCount'
  | 'adminClassroomCount'
  | 'moderatorClassroomCount'
  | 'memberClassroomCount'
  | 'followerClassroomCount'
  // home club related
  | 'homeMemberCount'
  // game set related
  | 'publicGameSetCount'
  | 'followersGameSetCount'
  | 'membersGameSetCount'
  // user items
  | 'avatarItemCount'
  | 'collectedFragmentCount'
  | 'giftCount'
  | 'donatedBreadCount'
  // activities
  | 'playedGameCount'
  | 'viewedSlideshowCount'
  | 'newNotificationCount'
  | 'newChatCount'
  // post related
  | 'userChannelPublicPostCount'
  | 'userChannelFollowersPostCount'
  | 'userChannelFriendsPostCount'
  | 'userChannelSelfPostCount'
  // invitation count
  | 'managedInvitationCount'
  | 'exclusiveInvitationCount'
  | 'managedInvitationExtraLimit'
  | 'exclusiveInvitationLimit'
  | 'smartParentsPlanReferralCount'
  | 'weaMembershipPlanReferralCount'
  // others
  | 'charisma'
  | 'memberCount'
  | 'memberEnrollmentCount'
  | 'followeeCount'
  | 'followerCount';

type Statistics = Partial<Record<StatisticsKey, number>>;

export enum EnumAgeGroup {
  '17-24' = '17-24',
  '25-29' = '25-29',
  '30-39' = '30-39',
  '40-49' = '40-49',
  '50-59' = '50-59',
  '60-69' = '60-69',
  '70-79' = '70-79',
  '80+' = '80+',
}

export interface ProfileJson<T = number> {
  userId: UserId;
  registeredAt: T;
  lastModifiedAt: T;
  name: string;
  role: UserRole;
  avatar: Avatar;
  profilePicture: AttachmentImage | null;
  banner: AttachmentImage | null;
  dateOfBirth?: string | null;
  yearOfBirth?: string | null;
  birthday?: string | null;
  ageGroup: EnumAgeGroup | null;
  gender?: null | Gender;
  aboutMe: string | null;
  industryOfWork?: null; // TODO: missing backend feature
  organization?: string | null;
  topics?: string[]; // size = [0, 10]
  level?: EducationLevel | null;
  region?: string;
  city?: string | null;
  privacySettings?: PrivacySettings;
  favouritePetId: string | null;
  exp: ExpJson;
  expRewardLevel: number;
  // var buddy: Buddy?  // null
  statistics: Statistics;
  willDeleteAt?: T;
  lastActiveAt: T;
  // buddy: Buddy;
  isSuspended?: boolean;

  // local variable
  profilePicturePath?: string;
}

export type ProfileIdentifier = 'userId';

export class Profile extends RepositoryElement implements ProfileJson<Date> {
  userId!: UserId;
  registeredAt!: Date;
  lastModifiedAt!: Date;
  name!: string;
  role!: UserRole;
  avatar!: Avatar;
  profilePicture!: AttachmentImage | null;
  banner!: AttachmentImage | null;
  dateOfBirth?: string | null;
  yearOfBirth?: string | null;
  birthday?: string | null;
  ageGroup!: EnumAgeGroup | null;
  gender?: Gender | null;
  aboutMe!: string | null;
  industryOfWork?: null; // TODO: missing backend feature
  organization?: string | null;
  topics?: string[];
  level?: EducationLevel | null;
  region?: RegionsCode;
  city?: string | null;
  privacySettings?: PrivacySettings;
  favouritePetId!: string | null;
  exp!: Exp;
  expRewardLevel!: number;
  statistics!: Statistics;
  willDeleteAt?: Date;
  lastActiveAt!: Date;

  constructor(profile: ProfileJson<Date | number>) {
    super();
    const { exp, ...rest } = deepTransformTimestamp(
      profile,
    ) as ProfileJson<Date>;
    Object.assign(this, rest);
    this.exp = new Exp(exp);
  }

  id() {
    return this.userId;
  }

  toJSON(): ProfileJson {
    return deepTransformDate(this) as unknown as ProfileJson;
  }

  get localCurrency() {
    return getLocalCurrencyById(this.region);
  }

  get worksForJoyajoy() {
    return (
      this.role === UserRole.Admin ||
      this.role === UserRole.Staff ||
      this.role === UserRole.Sales
    );
  }

  get isVerified() {
    return (
      this.role === UserRole.Admin ||
      this.role === UserRole.Staff ||
      this.role === UserRole.Sales ||
      this.role === UserRole.Partner ||
      this.role === UserRole.VerifiedUser
    );
  }

  get rootSpaceCount() {
    return (
      (this.statistics.rootPublicClassroomCount ?? 0) +
      (this.statistics.rootPrivateAndVisibleClassroomCount ?? 0) +
      (this.statistics.rootPrivateAndHiddenClassroomCount ?? 0) +
      (this.statistics.rootPersonalClassroomCount ?? 0)
    );
  }

  get albumCount() {
    return (
      (this.statistics.publicGameSetCount ?? 0) +
      (this.statistics.followersGameSetCount ?? 0) +
      (this.statistics.membersGameSetCount ?? 0)
    );
  }

  get referralCount() {
    return (
      (this.statistics.managedInvitationCount ?? 0) +
      (this.statistics.exclusiveInvitationCount ?? 0)
    );
  }

  static sortByLastActiveAt(this: void, p1: Profile, p2: Profile) {
    return p1.lastActiveAt < p2.lastActiveAt ? 1 : -1;
  }

  static get mockProfile() {
    const mockProfileData: ProfileJson<number> = {
      userId: 'ab554c74-8df7-4c7a-9d8c-4708feeeb8b5',
      name: 'John Doe',
      registeredAt: Date.now(),
      lastModifiedAt: Date.now(),
      ageGroup: EnumAgeGroup['25-29'],
      aboutMe: 'Just a mock profile for testing purposes.',
      dateOfBirth: '1995-05-15',
      yearOfBirth: '1995',
      birthday: 'May 15',
      gender: Gender.Male,
      organization: 'Mock Organization',
      profilePicture: null,
      banner: null,
      avatar: {
        body: AvatarBodiesItemNo.a1,
        neck: AvatarNecksItemNo.a101,
        aura: AvatarAurasItemNo.a15,
        hat: AvatarHatsItemNo.a1,
        arms: AvatarArmsItemNo.a1,
        feet: AvatarFeetItemNo.a97,
        wings: AvatarWingsItemNo.a108,
      },
      role: UserRole.User,
      region: 'US' as RegionsCode,
      city: 'Mock City',
      level: null,
      topics: ['Technology', 'Gaming'],
      favouritePetId: 'mockPetId',
      privacySettings: {
        birthday: PrivacyScope.Public,
        gender: PrivacyScope.Public,
      },
      exp: {
        charm: { plays: 10, likes: 5 },
        socialContribution: {
          browsingActivities: 20,
          volunteerActivities: 5,
          givingCheers: 15,
          esgPlants: 3,
          encouragements: 10,
          multiplayerGames: 8,
          gameCreations: 2,
        },
        knowledge: {
          games: 12,
          competitions: 4,
        },
      },
      expRewardLevel: 10,
      statistics: {
        storedImageCount: 5,
        createdGameCount: 2,
        playedGameCount: 20,
      },
      lastActiveAt: Date.now(),
    };

    return new Profile(mockProfileData);
  }
}

export const ProfileExample: Profile = new Profile({
  userId: 'userId',
  registeredAt: 1728528788342,
  lastModifiedAt: 1728528788342,
  name: 'user',
  role: UserRole.User,
  avatar: {
    feet: null,
    wings: null,
    hat: null,
    arms: null,
    body: null,
    neck: null,
    aura: null,
  },
  profilePicture: AttachmentImageExample,
  banner: null,
  dateOfBirth: null,
  ageGroup: null,
  gender: null,
  aboutMe: null,
  industryOfWork: null,
  organization: null,
  topics: [],
  level: null,
  region: RegionsCode.US,
  city: null,
  favouritePetId: null,
  exp: {
    charm: {},
    knowledge: {
      games: 699,
    },
    socialContribution: {
      gameCreations: 1107,
      multiplayerGames: 230,
    },
  },
  expRewardLevel: 0,
  statistics: {},
  lastActiveAt: 1728528788339,
});
